/* eslint-disable no-console */
/**
 * Centralized logging service.
 *
 * Usage:
 *   logger.debug('message', data1, data2);
 *   logger.info('User logged in', userId);
 *   logger.warn('Deprecated API', { endpoint });
 *   logger.error('Failed to fetch', error);
 *
 * With options (last argument, not printed):
 *   logger.debug('Sensitive data', payload, { forceProd: true });
 *
 * Debug mode in production:
 *   localStorage.setItem('duos:debug', 'true');
 *
 * Note: logger.error() automatically sends errors to Sentry in production.
 */

import { Sentry } from '@/integrations/sentry';

interface LogOptions {
  /** Force console output even in production */
  forceProd?: boolean;
}

const isLogOptions = (arg: unknown): arg is LogOptions => {
  if (typeof arg !== 'object' || arg === null || Array.isArray(arg)) {
    return false;
  }
  const keys = Object.keys(arg);
  return keys.length === 1 && keys[0] === 'forceProd';
};

const isDev = (): boolean => import.meta.env.DEV;
const isProd = (): boolean => import.meta.env.PROD;

type LogArgs = unknown[];

interface ParsedArgs {
  args: LogArgs;
  options: LogOptions;
}

const parseArgs = (args: LogArgs): ParsedArgs => {
  if (args.length === 0) {
    return { args: [], options: {} };
  }

  const lastArg = args[args.length - 1];

  if (isLogOptions(lastArg)) {
    return {
      args: args.slice(0, -1),
      options: lastArg,
    };
  }

  return { args, options: {} };
};

const shouldLog = (options: LogOptions): boolean => {
  // Force prod option overrides environment check
  if (options.forceProd) {
    return true;
  }

  return isDev();
};

const createLogMethod =
  (
    level: 'debug' | 'info' | 'warn' | 'error' | 'log',
    consoleFn: typeof console.log,
  ) =>
  (...rawArgs: LogArgs): void => {
    const { args, options } = parseArgs(rawArgs);

    if (!shouldLog(options)) {
      return;
    }

    const prefix = `[${level.toUpperCase()}]`;
    consoleFn(prefix, ...args);
  };

/**
 * Error logging with automatic Sentry reporting in production.
 */
const createErrorMethod = () => {
  return (...rawArgs: LogArgs): void => {
    const { args, options } = parseArgs(rawArgs);

    // Always log to console if shouldLog
    if (shouldLog(options)) {
      console.error('[ERROR]', ...args);
    }

    // Send to Sentry in production
    if (isProd() && args.length > 0) {
      const firstArg = args[0];

      if (firstArg instanceof Error) {
        // If first argument is Error, capture it with context
        Sentry.captureException(firstArg, {
          extra: {
            additionalArgs: args.slice(1),
          },
        });
      } else {
        // For non-Error arguments, capture as message
        const message =
          typeof firstArg === 'string' ? firstArg : JSON.stringify(firstArg);
        Sentry.captureMessage(message, {
          level: 'error',
          extra: {
            args: args.slice(1),
          },
        });
      }
    }
  };
};

export const logger = {
  debug: createLogMethod('debug', console.debug),
  info: createLogMethod('info', console.info),
  warn: createLogMethod('warn', console.warn),
  error: createErrorMethod(),
  log: createLogMethod('log', console.log),
};
